import axios from 'axios';
import React, { useState, useRef } from 'react';
import { FiTrash } from 'react-icons/fi';

interface VoiceRecorderProps {
  onRecordingComplete: (url: string) => void;
  onRemoveRecording: (blob: Blob) => void;
}

interface UploadResponse {
  s3_url: string;
  message?: string;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onRecordingComplete,
  onRemoveRecording
}) => {
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const currentBlobRef = useRef<Blob | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const generateRandomName = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const length = 8;
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `audio_${result}.mp3`; // Added .mp3 extension
  };

  const startRecording = async (): Promise<void> => {
    try {
      const stream: MediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Set up MediaRecorder with MP3 mime type if supported
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=mp3')
        ? 'audio/webm;codecs=mp3'
        : 'audio/webm';

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 128000 // 128 kbps for better quality
      });

      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e: BlobEvent): void => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = (): void => {
        const audioBlob: Blob = new Blob(chunksRef.current, { type: mimeType });
        const audioUrl: string = URL.createObjectURL(audioBlob);
        currentBlobRef.current = audioBlob;
        // onRecordingComplete(audioBlob);
        handleUploadAudio(audioBlob);
      };

      // Record in smaller chunks for better handling
      mediaRecorderRef.current.start(1000); // Record in 1-second chunks
      setIsRecording(true);
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError('Error accessing microphone. Please ensure you have granted microphone permissions.');
    }
  };

  const stopRecording = (): void => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  const handleRemove = (): void => {
    if (currentBlobRef.current) {
      onRemoveRecording(currentBlobRef.current);
    }
    setAudioURL(null);
    chunksRef.current = [];
    currentBlobRef.current = null;
    setError('');
    if (mediaRecorderRef.current && isRecording) {
      stopRecording();
    }
  };

  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          // Remove the data URL prefix
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        } else {
          reject(new Error('Failed to convert to base64'));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleUploadAudio = async (audioBlob: Blob): Promise<void> => {
    setIsUploading(true);
    setError('');
    const name = generateRandomName();

    try {
      const base64 = await blobToBase64(audioBlob);

      const payload = {
        file_name: name,
        content_type: 'audio/mpeg',
        file_data: base64,
      };

      console.log('Uploading audio with filename:', name);

      const response = await axios.post<UploadResponse>(
        'https://e6w2t1d2f1.execute-api.us-east-1.amazonaws.com/dev/upload',
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

      if (response.data && response.data.s3_url) {
        onRecordingComplete(response.data.s3_url)
        setAudioURL(response.data.s3_url);
      } else {
        throw new Error('Invalid server response');
      }
    } catch (error) {
      console.error('Upload error:', error);

      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED') {
          setError('Upload timed out. Please try again.');
        } else if (error.response) {
          setError(`Upload failed: ${error.response.data.message || 'Please try again.'}`);
        } else if (error.request) {
          setError('Network error. Please check your internet connection.');
        } else {
          setError('Failed to upload audio. Please try again.');
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="flex flex-col items-center space-y-4 p-4 border rounded-lg bg-white shadow-sm mb-4">
      {audioURL ? (
        <div className="w-full max-w-md flex flex-col gap-4">
          <div className="flex justify-between items-center gap-4">

            <audio src={audioURL} controls className="w-full" />

            <button
              onClick={handleRemove}
              className="flex items-center text-red-500 hover:text-red-600"
            >
              <FiTrash className="mr-2" />
            </button>
          </div>
        </div>
      ) : (
        <div className="flex space-x-4">
          <button
            onClick={isRecording ? stopRecording : startRecording}
            className={`flex items-center space-x-2 px-4 py-2 rounded-full ${isRecording
              ? 'bg-red-500 hover:bg-red-600 text-white'
              : 'bg-blue-500 hover:bg-blue-600 text-white'
              } transition-colors duration-200 disabled:opacity-50`}
            disabled={isUploading}
            type="button"
          >
            <span className="mr-2">
              {isRecording ? '⬛' : '🎤'}
            </span>
            <span>{isRecording ? 'Stop Recording' : 'Start Recording'}</span>
          </button>
        </div>
      )}

      {isRecording && (
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          <span className="text-red-500">Recording...</span>
        </div>
      )}

      {isUploading && (
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
          <span className="text-blue-500">Uploading...</span>
        </div>
      )}

      {error && !audioURL && (
        <p className="text-red-500 text-sm">{error}</p>
      )}
    </div>
  );
};

export default VoiceRecorder;